import flatpickr from 'flatpickr';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnceWhen } from 'rooks';
import { createFooterButtons } from '@/datetime/flatpickr/flatpickrButtons.util';
import { positionFlatpickrCalendar } from '@/datetime/flatpickr/flatpickrPositioning.util';
import {
  createDateWithNewMonthYear,
  momentToTimezoneNaiveDate,
  timezoneNaiveDateToMoment,
} from '@/datetime/flatpickr/flatpickrTimezones.util';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import type { Moment } from 'moment-timezone';
import type { RefObject } from 'react';
import type { CreateFlatpickrFooter } from '@/datetime/flatpickr/flatpickrButtons.util';
import type { Timezone } from '@/datetime/timezone.service';

export interface UseFlatpickrParameters {
  /**
   * Input element to attach flatpickr to
   */
  anchorRef: React.RefObject<HTMLElement | null>;

  /**
   * Current datetime value
   */
  value: Moment | null | undefined;

  /**
   * The tz to be used for datetime value
   */
  timezone: Timezone;

  /**
   * Format string used to display the datetime value
   */
  localizedDateFormat: string;

  /**
   * Callback to handle date selection
   * @param date The selected date
   */
  onDateSelect: (date: Moment | undefined) => void;

  /**
   * Callback to handle manual input
   * @param input The input value
   * @returns The parsed date or undefined if parsing fails
   */
  onManualInput?: (input: string) => Moment | undefined;

  /**
   * Flag to enable the seconds input
   */
  enableSeconds?: boolean;

  /**
   * Flag to enable the time input
   */
  enableTime?: boolean;

  /**
   * Flag to display time in 24-hour format
   */
  enableTime24hr?: boolean;

  /**
   * Flag to indicate if the picker is used on a modal
   */
  isInsideModal?: boolean;

  /**
   * The minimum date that a user can start picking from (inclusive).
   */
  minDate?: Moment;

  /**
   * The maximum date that a user can pick to (inclusive).
   */
  maxDate?: Moment;

  /**
   * Adjusts the step for the minute input
   */
  minuteIncrement?: number;

  /**
   * Flag to render the picker inline
   */
  inline?: boolean;

  /**
   * Flag to control whether the flatpickr is open. If undefined, the picker will be open by default.
   */
  isOpen?: boolean;

  /**
   * Function to render the footer of the flatpickr instance.
   */
  renderFooter?: CreateFlatpickrFooter;
  /**
   * Flag to control whether the date should be dynamically updated when only month/year changes in the picker.
   * When false, clicking "Done" will not auto-update the selected date to match the current month/year.
   * Defaults to true.
   */
  autoUpdateDate?: boolean;
}

export const useFlatpickr = ({
  anchorRef,
  value,
  timezone,
  localizedDateFormat,
  enableSeconds,
  enableTime,
  enableTime24hr,
  onDateSelect,
  onManualInput,
  isInsideModal,
  minDate,
  maxDate,
  minuteIncrement = 1,
  inline,
  isOpen,
  renderFooter,
  autoUpdateDate = true,
}: UseFlatpickrParameters): RefObject<FlatpickrInstance | null> => {
  const { t } = useTranslation();
  const flatpickrRef = useRef<FlatpickrInstance | null>(null);
  const buttonCleanupRef = useRef<(() => void) | null>(null);

  const formatDate = useCallback(
    (date: Date, _format?: string): string => {
      const targetMoment = timezoneNaiveDateToMoment(date, timezone);
      return targetMoment.format(localizedDateFormat);
    },
    [timezone, localizedDateFormat],
  );

  const parseDate = useCallback(
    (dateString: string, _format?: string, _timeless?: boolean): Date => {
      const parsedMoment = onManualInput?.(dateString);
      if (!parsedMoment) {
        // Return a default date if parsing fails
        return new Date();
      }
      const timezoneNaiveDate = momentToTimezoneNaiveDate(parsedMoment, timezone);
      flatpickrRef.current?.setDate(timezoneNaiveDate, false);
      return timezoneNaiveDate;
    },
    [onManualInput, timezone],
  );

  const handleReady = useCallback(
    (_dates: Date[], _dateStr: string, instance: FlatpickrInstance) => {
      if (typeof renderFooter === 'function') {
        renderFooter({
          flatpickrInstance: instance,
        });
        return;
      }
      const onCancel = () => {
        const resetDate = value ? momentToTimezoneNaiveDate(value, timezone) : undefined;
        if (resetDate) {
          instance.setDate(resetDate, false);
        } else {
          instance.clear();
        }
        instance.close();
      };

      const onDone = () => {
        if (autoUpdateDate) {
          const selectedDate = instance.selectedDates?.[0];
          if (!selectedDate) {
            return;
          }

          const newDate = createDateWithNewMonthYear(selectedDate, instance.currentYear, instance.currentMonth);
          const zonedDate = timezoneNaiveDateToMoment(newDate, timezone);
          onDateSelect(zonedDate);
        }
        instance.close();
      };
      buttonCleanupRef.current = createFooterButtons({ flatpickrInstance: instance, t, onCancel, onDone });
    },
    [renderFooter, t, value?.valueOf(), timezone, onDateSelect],
  );

  const handleDateChange = useCallback(
    (selectedDates: Date[]) => {
      if (selectedDates.length > 0) {
        const selectedDate = selectedDates[0];
        const zonedDate = timezoneNaiveDateToMoment(selectedDate, timezone);
        onDateSelect(zonedDate);
      } else {
        // No dates selected - clear the date
        onDateSelect(undefined);
      }
    },
    [onDateSelect, timezone],
  );

  /**
   * Updates the visual selection when navigating months/years.
   * This provides immediate visual feedback showing which date will be selected
   * when the user clicks "Done" after navigating to a different month/year.
   */
  const handleMonthYearChange = useCallback(
    (_selectedDates: Date[], _dateStr: string, instance: FlatpickrInstance) => {
      if (!autoUpdateDate) return;

      const currentSelection = instance.selectedDates?.[0];
      if (!currentSelection) return;

      const newDate = createDateWithNewMonthYear(currentSelection, instance.currentYear, instance.currentMonth);

      // Update selection visually without triggering onChange
      instance.setDate(newDate, false);
    },
    [autoUpdateDate],
  );

  const getModalContainer = useCallback((): HTMLElement | null => {
    return (anchorRef.current?.closest('[data-state="open"][role="dialog"]') as HTMLElement) || null;
  }, [anchorRef]);

  /**
   * Sync flatpickr display when value, timezone, or date format changes.
   *
   * config.formatDate is updated first so that any subsequent setDate call
   * uses the correct (potentially new) format string immediately.
   *
   * setDate is always called (no needsSynced guard) so that format-only changes
   * (e.g. switching 12h ↔ 24h) reliably refresh the input text. Flatpickr's
   * setDate/buildAndUpdateOutput is the only path that writes to input.value;
   * set('time_24hr') only redraws the calendar popup, not the text field.
   */
  useEffect(() => {
    if (flatpickrRef.current) {
      flatpickrRef.current.config.formatDate = formatDate;
      if (value) {
        const jsDate = momentToTimezoneNaiveDate(value, timezone);
        flatpickrRef.current.setDate(jsDate, false);
      } else {
        flatpickrRef.current.clear();
      }
    }
  }, [value?.valueOf(), timezone, localizedDateFormat, formatDate]);

  useEffect(() => {
    if (flatpickrRef.current) {
      flatpickrRef.current.config.parseDate = parseDate;
      flatpickrRef.current.config.onChange = [handleDateChange];
      flatpickrRef.current.config.onMonthChange = [handleMonthYearChange];
      flatpickrRef.current.config.onYearChange = [handleMonthYearChange];
      flatpickrRef.current.config.formatDate = formatDate;
    }
  }, [parseDate, handleDateChange, handleMonthYearChange, formatDate]);

  useEffect(() => {
    if (flatpickrRef.current && isOpen !== undefined) {
      if (isOpen) {
        flatpickrRef.current.open();
      } else {
        flatpickrRef.current.close();
      }
    }
  }, [isOpen]);

  useEffectOnceWhen(() => {
    if (!anchorRef.current) return;

    const appendTo = isInsideModal ? getModalContainer() : null;
    const initialDate = value ? momentToTimezoneNaiveDate(value, timezone) : undefined;

    flatpickrRef.current = flatpickr(anchorRef.current, {
      ...(!!appendTo && {
        appendTo,
        position: (fp: FlatpickrInstance, relativeEl: HTMLElement | undefined) => {
          positionFlatpickrCalendar(fp, relativeEl, appendTo);
        },
      }),
      allowInput: true,
      clickOpens: true,
      closeOnSelect: true,
      dateFormat: 'Z', // Use ISO format internally
      defaultDate: initialDate,
      disableMobile: true,
      enableSeconds,
      enableTime,
      formatDate,
      parseDate,
      time_24hr: enableTime24hr,
      inline,
      minuteIncrement,
      onChange: handleDateChange,
      onClose: () => {
        anchorRef.current?.blur();
      },
      onMonthChange: handleMonthYearChange,
      onYearChange: handleMonthYearChange,
      onReady: handleReady,
      ...(minDate && { minDate: momentToTimezoneNaiveDate(minDate, timezone) }),
      ...(maxDate && { maxDate: momentToTimezoneNaiveDate(maxDate, timezone) }),
    });

    return () => {
      buttonCleanupRef.current?.();
      buttonCleanupRef.current = null;
      flatpickrRef.current?.destroy();
      flatpickrRef.current = null;
    };
  }, !!anchorRef.current);

  return flatpickrRef;
};
